import React, { useEffect, useMemo, useState } from 'react';
import { FileTextOutlined, PrinterOutlined, UploadOutlined } from '@ant-design/icons';
import { usePrintQueue } from '../../context/PrintQueueContext';
import { putPrintQueueBlob } from '../../services/printQueueStore';
import {
  getDefaultQzSettings,
  getQzPrintReadiness,
  loadQzPrintSettings,
  printHtmlWithQz,
  reconcileQzPrintSettings,
  saveQzPrintSettings,
} from '../../services/qzPrintService';
import QzPrinterSettingsPanel from '../packingPrint/QzPrinterSettingsPanel';
import {
  buildGeneralCalibrationHtml,
  buildFittedGeneralLetterHtml,
  GENERAL_PRINT_SIZES,
  getGeneralPrintSize,
} from '../../utils/generalPrintSizes';
import {
  buildGeneralPrintHtmlFromTextFile,
  classifyGeneralPrintFile,
  convertImageFileToPdfBlob,
  describeUnsupportedGeneralPrintFile,
  GENERAL_PRINT_FILE_ACCEPT,
  inspectPdfBlob,
} from '../../utils/generalPrintFileSupport';
import './GeneralPrintPanel.css';

const GENERAL_PROFILE_KEYS = GENERAL_PRINT_SIZES.map((size) => size.profileKey);

export default function GeneralPrintPanel({
  sizeKey = 'A4',
  copies = 1,
  pageRanges = '',
  notes = '',
  onSessionPatch,
  onOpenQueue,
}) {
  const printQueue = usePrintQueue();
  const size = useMemo(() => getGeneralPrintSize(sizeKey), [sizeKey]);
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const [letterBody, setLetterBody] = useState(notes || '');
  const [qzStatus, setQzStatus] = useState({
    ready: false,
    message: 'Checking QZ Tray…',
    printerDetails: [],
  });
  const [qzSettings, setQzSettings] = useState(() => loadQzPrintSettings());

  useEffect(() => {
    let active = true;
    getQzPrintReadiness({ force: true }).then((readiness) => {
      if (!active) return;
      setQzStatus(readiness);
      if (readiness.ready) {
        setQzSettings((previous) => {
          const next = reconcileQzPrintSettings(previous, readiness.printerDetails);
          saveQzPrintSettings(next);
          return next;
        });
      }
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    setLetterBody(notes || '');
  }, [notes]);

  const patch = (partial) => onSessionPatch?.(partial);

  const routingForSize = () =>
    qzSettings[size.profileKey] || getDefaultQzSettings(size.profileKey);

  const enqueueHtml = async (html, label) => {
    printQueue.enqueueGeneralJob({
      kind: 'general-html',
      profileKey: size.profileKey,
      sizeKey: size.key,
      label,
      recipe: { html, copies, sizeKey: size.key },
      routing: routingForSize(),
    });
    setStatus(`${label} added to the mixed print queue.`);
    onOpenQueue?.();
  };

  const addCalibration = async () => {
    setBusy(true);
    try {
      await enqueueHtml(
        buildGeneralCalibrationHtml(size),
        `${size.label} calibration`,
      );
    } catch (error) {
      setStatus(error?.message || 'Unable to queue calibration page.');
    } finally {
      setBusy(false);
    }
  };

  const printCalibrationNow = async () => {
    setBusy(true);
    setStatus('Sending calibration page…');
    try {
      if (!qzStatus.ready) throw new Error(qzStatus.message || 'QZ Tray is not ready.');
      await printHtmlWithQz(
        size.profileKey,
        buildGeneralCalibrationHtml(size),
        routingForSize(),
      );
      setStatus(`${size.label} calibration sent to QZ.`);
    } catch (error) {
      setStatus(error?.message || 'Calibration print failed.');
    } finally {
      setBusy(false);
    }
  };

  const addLetter = async () => {
    setBusy(true);
    try {
      patch({ generalNotes: letterBody });
      await enqueueHtml(
        await buildFittedGeneralLetterHtml({
          title: 'London Smoke & Cure',
          body: letterBody,
          size,
        }),
        `${size.label} letter`,
      );
    } catch (error) {
      setStatus(error?.message || 'Unable to queue letter.');
    } finally {
      setBusy(false);
    }
  };

  const enqueuePdfBlob = async (blob, fileName, sourceLabel) => {
    const meta = await inspectPdfBlob(blob);
    const blobRef = `general-file-${globalThis.crypto?.randomUUID?.() || Date.now()}`;
    await putPrintQueueBlob(blobRef, blob, {
      temporary: false,
      name: fileName,
      sizeKey: size.key,
    });
    printQueue.enqueueGeneralJob({
      kind: 'general-pdf',
      profileKey: size.profileKey,
      sizeKey: size.key,
      label: `${size.label} ${sourceLabel} · ${fileName}`,
      recipe: {
        blobRef,
        fileName,
        pageCount: meta.pageCount,
        copies,
        pageRanges: sourceLabel === 'PDF' ? pageRanges : '',
        sizeKey: size.key,
      },
      routing: routingForSize(),
    });
    setStatus(
      `${fileName} (${meta.pageCount} page${meta.pageCount === 1 ? '' : 's'}) added on ${size.label}.`,
    );
    onOpenQueue?.();
  };

  const addPrintableFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setStatus(`Staging ${file.name}…`);
    try {
      const kind = classifyGeneralPrintFile(file);
      if (kind === 'unsupported') {
        throw new Error(describeUnsupportedGeneralPrintFile(file));
      }
      if (kind === 'docx') {
        const blobRef = `general-file-${globalThis.crypto?.randomUUID?.() || Date.now()}`;
        await putPrintQueueBlob(blobRef, file, {
          temporary: false,
          name: file.name,
          sizeKey: size.key,
        });
        printQueue.enqueueGeneralJob({
          kind: 'general-docx',
          profileKey: size.profileKey,
          sizeKey: size.key,
          label: `${size.label} Word · ${file.name}`,
          recipe: {
            blobRef,
            fileName: file.name,
            copies,
            sizeKey: size.key,
          },
          routing: routingForSize(),
        });
        setStatus(
          `${file.name} is in the queue line on ${size.label}. Export as PDF to print that slot; the rest of the line still runs.`,
        );
        onOpenQueue?.();
        return;
      }
      if (kind === 'pdf') {
        await enqueuePdfBlob(file, file.name, 'PDF');
        return;
      }
      if (kind === 'image') {
        setStatus(`Preparing ${file.name} as a ${size.label} PDF…`);
        const pdfBlob = await convertImageFileToPdfBlob(file, size);
        const pdfName = String(file.name || 'image').replace(
          /\.[^.]+$/,
          '.pdf',
        );
        await enqueuePdfBlob(pdfBlob, pdfName, 'Image');
        return;
      }
      if (kind === 'text' || kind === 'html') {
        const html = await buildGeneralPrintHtmlFromTextFile(file, size);
        await enqueueHtml(html, `${size.label} file · ${file.name}`);
        return;
      }
      throw new Error(describeUnsupportedGeneralPrintFile(file));
    } catch (error) {
      setStatus(error?.message || 'Unable to stage file.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="general-print-panel">
      <header className="general-print-panel__header">
        <div>
          <h2>General print</h2>
          <p>
            Queue A6, A4, A3, C5, or C6 work into the same mixed line as order jobs —
            PDFs, PNG and other images, text/HTML, calibration pages, short letters,
            and Word placeholders — without clearing Orders selection or filters.
          </p>
        </div>
      </header>

      <div className="general-print-panel__sizes" role="tablist" aria-label="Paper size">
        {GENERAL_PRINT_SIZES.map((entry) => (
          <button
            key={entry.key}
            type="button"
            role="tab"
            aria-selected={entry.key === size.key}
            className={`general-print-size ${entry.key === size.key ? 'is-active' : ''}`}
            onClick={() => patch({ generalSizeKey: entry.key })}
          >
            <strong>{entry.label}</strong>
            <span>
              {entry.widthMm} × {entry.heightMm} mm
            </span>
            <small>{entry.description}</small>
          </button>
        ))}
      </div>

      <div className="general-print-panel__controls toolkit-card">
        <label>
          Copies
          <input
            type="number"
            min={1}
            max={99}
            value={copies}
            disabled={busy}
            onChange={(event) =>
              patch({ generalCopies: Math.max(1, Math.min(99, Number(event.target.value) || 1)) })
            }
          />
        </label>
        <label>
          PDF pages
          <input
            type="text"
            value={pageRanges}
            disabled={busy}
            placeholder="All, or 1-4"
            onChange={(event) => patch({ generalPageRanges: event.target.value })}
          />
        </label>
        <div className="general-print-panel__routing">
          <strong>{size.label}</strong>
          <span>
            {(routingForSize().printer || size.defaultTray) +
              ' · ' +
              (routingForSize().printerTray || size.defaultTray)}
          </span>
        </div>
      </div>

      <div className="general-print-panel__actions">
        <label className="general-print-upload">
          <UploadOutlined />
          <span>
            <b>Add file to queue</b>
            <small>PDF, PNG, JPG, WEBP, GIF, BMP, TXT, CSV, HTML, or Word</small>
          </span>
          <input
            type="file"
            accept={GENERAL_PRINT_FILE_ACCEPT}
            disabled={busy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) addPrintableFile(file);
              event.target.value = '';
            }}
          />
        </label>
        <button
          type="button"
          className="toolkit-btn toolkit-btn--secondary"
          disabled={busy}
          onClick={addCalibration}
        >
          <PrinterOutlined /> Queue {size.label} calibration
        </button>
        <button
          type="button"
          className="toolkit-btn toolkit-btn--ghost"
          disabled={busy || !qzStatus.ready}
          onClick={printCalibrationNow}
        >
          Print calibration now
        </button>
      </div>

      <div className="general-print-panel__letter toolkit-card">
        <h3>
          <FileTextOutlined /> Short letter / notice
        </h3>
        <textarea
          rows={6}
          value={letterBody}
          disabled={busy}
          placeholder="Paste or type a notice — text and spacing shrink to fit one page…"
          onChange={(event) => {
            setLetterBody(event.target.value);
            patch({ generalNotes: event.target.value });
          }}
        />
        <p className="general-print-panel__letter-hint">
          Pasted text is never clipped: type size and spacing shrink until the notice fits one{' '}
          {size.label} page.
        </p>
        <button
          type="button"
          className="toolkit-btn toolkit-btn--primary"
          disabled={busy}
          onClick={addLetter}
        >
          Queue {size.label} letter
        </button>
      </div>

      {status ? <p className="general-print-panel__status">{status}</p> : null}
      <p className="general-print-panel__hint">
        {qzStatus.ready
          ? qzStatus.message
          : qzStatus.message || 'QZ Tray is not ready for direct print.'}{' '}
        Images are fitted to the selected paper size as a one-page PDF. Word (.docx)
        slots stay in the line for continuity and are skipped at print time until
        exported as PDF.
      </p>

      <QzPrinterSettingsPanel
        profileKeys={GENERAL_PROFILE_KEYS}
        settings={qzSettings}
        printerDetails={qzStatus.printerDetails || []}
        onChange={(next) => {
          setQzSettings(next);
          saveQzPrintSettings(next);
        }}
        onRecheck={async () => {
          const readiness = await getQzPrintReadiness({ force: true });
          setQzStatus(readiness);
          if (readiness.ready) {
            setQzSettings((previous) => {
              const next = reconcileQzPrintSettings(previous, readiness.printerDetails);
              saveQzPrintSettings(next);
              return next;
            });
          }
        }}
      />
    </section>
  );
}
